import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, clearSession, currentUser, currentRole } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { LogOut, Wallet, Star, CheckCircle2, LogIn, LogOutIcon, History, Bell, MessageSquareWarning, Shield, Phone, User, MapPin, KeyRound } from "lucide-react";

const KYC_PILL = {
  approved: "bg-emerald-500/20 text-emerald-400",
  submitted: "bg-amber-500/20 text-amber-400",
  under_review: "bg-amber-500/20 text-amber-400",
  rejected: "bg-red-500/20 text-red-400",
  suspended: "bg-red-500/20 text-red-400",
};

export default function WorkerDashboard() {
  const nav = useNavigate();
  const [me, setMe] = useState(currentUser());
  const [assignments, setAssignments] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [history, setHistory] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [availability, setAvailability] = useState(me?.availability || "offline");
  const [closureBooking, setClosureBooking] = useState(null);
  const [closureCode, setClosureCode] = useState("");
  const [newComplaint, setNewComplaint] = useState({ category: "other", description: "" });
  const [startCodeBooking, setStartCodeBooking] = useState(null);
  const [startCode, setStartCode] = useState("");

  const guard = () => {
    const role = currentRole();
    const t = localStorage.getItem("karmivo_token");
    if (!t || role !== "worker") { nav("/worker/login"); return false; }
    return true;
  };

  const load = async () => {
    if (!guard()) return;
    try {
      const [m, a, p, h, r, c, n] = await Promise.all([
        api.get("/worker/me"),
        api.get("/worker/assignments"),
        api.get("/worker/payouts"),
        api.get("/worker/history"),
        api.get("/worker/ratings"),
        api.get("/worker/complaints"),
        api.get("/worker/notifications"),
      ]);
      setMe(m.data); setAssignments(a.data); setPayouts(p.data);
      setHistory(h.data); setRatings(r.data); setComplaints(c.data); setNotifs(n.data);
      setAvailability(m.data.availability);
      localStorage.setItem("karmivo_user", JSON.stringify(m.data));
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        toast.error(e.response?.data?.detail || "Session expired");
        clearSession();
        nav("/worker/login");
      } else {
        toast.error("Load failed");
      }
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const setAvail = async (v) => {
    try { await api.post(`/worker/availability`, null, { params: { status: v } }); setAvailability(v); toast.success("Updated"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const act = async (id, action) => {
    try { await api.post(`/worker/assignments/${id}/${action}`); toast.success(action); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submitClosure = async () => {
    try {
      await api.post(`/worker/closure/submit`, { booking_id: closureBooking.id, code: closureCode });
      toast.success("Closure verified. Booking completed!");
      setClosureBooking(null); setClosureCode(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid code"); }
  };

  const arrive = async (bookingId) => {
    try { await api.post(`/worker/arrived`, null, { params: { booking_id: bookingId } }); toast.success("Arrival logged. Customer notified to pay remaining amount."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submitStartCode = async () => {
    try {
      await api.post(`/worker/work-start/verify`, { booking_id: startCodeBooking.id, code: startCode });
      toast.success("Work started!"); setStartCodeBooking(null); setStartCode(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid code"); }
  };

  const submitComplaint = async () => {
    try {
      await api.post(`/complaints`, { category: newComplaint.category, description: newComplaint.description });
      toast.success("Complaint filed");
      setNewComplaint({ category: "other", description: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!me) return null;

  const kycApproved = me.kyc_status === "approved";

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 pb-10">
      <header className="sticky top-0 z-20 bg-slate-900/95 backdrop-blur border-b border-slate-800 px-5 py-3 flex items-center justify-between">
        <div>
          <div className="font-display font-extrabold text-xl">KARMIVO <span className="text-orange-400">Worker</span></div>
          <div className="text-xs text-slate-400">Hi, {me.name}</div>
        </div>
        <Button variant="ghost" size="icon" onClick={() => { clearSession(); nav("/"); }} data-testid="w-logout"><LogOut size={18}/></Button>
      </header>

      <main className="max-w-xl mx-auto px-4 py-4 space-y-4">
        {!kycApproved && (
          <Card className="p-3 bg-amber-500/10 border-amber-500/30" data-testid="kyc-banner">
            <div className="flex items-center gap-2 text-amber-300 text-sm">
              <Shield size={16}/><span>Your KYC is <b>{me.kyc_status}</b>. You will receive assignments only after admin approval.</span>
            </div>
          </Card>
        )}

        <Card className="p-4 bg-slate-800 border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">KYC Status</div>
              <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs font-bold ${KYC_PILL[me.kyc_status] || "bg-slate-700"}`} data-testid="kyc-badge">{me.kyc_status}</span>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Availability</div>
              <Select value={availability} onValueChange={setAvail} disabled={!kycApproved}>
                <SelectTrigger className="bg-slate-900 border-slate-700 w-36" data-testid="availability-select">
                  <SelectValue placeholder="Set availability">{availability}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="busy">Busy</SelectItem>
                  <SelectItem value="offline">Offline</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-4 text-center">
            <div><div className="text-slate-400 text-[10px] uppercase">Jobs</div><div className="font-bold text-lg" data-testid="stat-jobs">{me.completed_jobs || 0}</div></div>
            <div><div className="text-slate-400 text-[10px] uppercase">Rating</div><div className="font-bold text-lg flex items-center justify-center gap-1" data-testid="stat-rating"><Star size={14} className="text-yellow-400"/>{Number(me.rating_avg ?? 0).toFixed(1)}</div></div>
            <div><div className="text-slate-400 text-[10px] uppercase">Earnings</div><div className="font-bold text-lg" data-testid="stat-earnings">₹{me.earnings_total || 0}</div></div>
          </div>
        </Card>

        <Tabs defaultValue="assignments" className="w-full">
          <TabsList className="bg-slate-800 border border-slate-700 w-full grid grid-cols-5 h-11">
            <TabsTrigger value="assignments" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white text-xs" data-testid="tab-assignments">Jobs</TabsTrigger>
            <TabsTrigger value="history" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white text-xs" data-testid="tab-history">History</TabsTrigger>
            <TabsTrigger value="payouts" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white text-xs" data-testid="tab-payouts">Payouts</TabsTrigger>
            <TabsTrigger value="ratings" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white text-xs" data-testid="tab-ratings">Ratings</TabsTrigger>
            <TabsTrigger value="more" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white text-xs" data-testid="tab-more">More</TabsTrigger>
          </TabsList>

          <TabsContent value="assignments" className="space-y-3 mt-3">
            {assignments.length === 0 && <Card className="p-4 bg-slate-800 border-slate-700 text-sm text-slate-400" data-testid="no-assignments">No assignments yet.{!kycApproved ? " KYC approval required first." : " Set yourself to Available to start receiving work."}</Card>}
            {assignments.map(a => (
              <Card key={a.id} className="p-4 bg-slate-800 border-slate-700" data-testid={`assignment-${a.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-mono font-bold">{a.booking?.code}</div>
                    <div className="text-xs text-slate-400">{a.booking?.pricing?.service_label} • {a.booking?.scheduled_date}</div>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded font-semibold uppercase ${a.status === "pending" ? "bg-amber-500/20 text-amber-300" : a.status === "checked_in" ? "bg-blue-500/20 text-blue-300" : a.status === "checked_out" ? "bg-emerald-500/20 text-emerald-300" : "bg-slate-700"}`}>{a.status}</span>
                </div>
                <div className="text-sm text-slate-300 mt-2 flex items-start gap-1.5"><Phone size={12} className="mt-1 text-slate-500"/>{a.booking?.location}</div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {a.status === "pending" && <>
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => act(a.id, "accept")} data-testid={`accept-${a.id}`}>Accept</Button>
                    <Button size="sm" variant="outline" className="border-slate-600" onClick={() => act(a.id, "reject")} data-testid={`reject-${a.id}`}>Reject</Button>
                  </>}
                  {a.status === "accepted" && <Button size="sm" className="col-span-2 bg-orange-500 hover:bg-orange-600" onClick={() => act(a.id, "checkin")} data-testid={`checkin-${a.id}`}><LogIn size={14} className="mr-1"/>Check In</Button>}
                  {a.status === "checked_in" && <>
                    <Button size="sm" className="bg-purple-600 hover:bg-purple-700" onClick={() => arrive(a.booking_id)} data-testid={`arrived-${a.id}`}><MapPin size={14} className="mr-1"/>I've Arrived</Button>
                    <Button size="sm" variant="outline" className="border-slate-600" onClick={() => act(a.id, "checkout")} data-testid={`checkout-${a.id}`}><LogOutIcon size={14} className="mr-1"/>Check Out</Button>
                  </>}
                  {(a.status === "arrived" || (a.booking?.payment_status === "fully_paid" && !["working","checked_out"].includes(a.status))) && (
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button size="sm" className="col-span-2 bg-blue-600 hover:bg-blue-700" onClick={() => setStartCodeBooking(a.booking)} data-testid={`startcode-${a.id}`}><KeyRound size={14} className="mr-1"/>Enter Work Start Code</Button>
                      </DialogTrigger>
                      <DialogContent className="bg-slate-900 border-slate-700 text-slate-100">
                        <DialogHeader><DialogTitle>Enter Work Start Code</DialogTitle></DialogHeader>
                        <p className="text-sm text-slate-400">Ask the customer for their 6-digit Work Start Code (sent after full payment).</p>
                        <Input value={startCode} onChange={e => setStartCode(e.target.value)} placeholder="000000" className="bg-slate-800 border-slate-700 font-mono text-2xl tracking-widest text-center" maxLength={6} data-testid="startcode-input"/>
                        <Button onClick={submitStartCode} className="bg-blue-600 hover:bg-blue-700" data-testid="startcode-submit-btn">Start Work</Button>
                      </DialogContent>
                    </Dialog>
                  )}
                  {a.status === "working" && <Button size="sm" className="col-span-2 bg-orange-500" onClick={() => act(a.id, "checkout")} data-testid={`checkout-${a.id}`}><LogOutIcon size={14} className="mr-1"/>Finish & Check Out</Button>}
                  {a.status === "checked_out" && (
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button size="sm" className="col-span-2 bg-emerald-600 hover:bg-emerald-700" onClick={() => setClosureBooking(a.booking)} data-testid={`closure-${a.id}`}><CheckCircle2 size={14} className="mr-1"/>Submit Closure Code</Button>
                      </DialogTrigger>
                      <DialogContent className="bg-slate-900 border-slate-700 text-slate-100">
                        <DialogHeader><DialogTitle>Enter Closure Code</DialogTitle></DialogHeader>
                        <p className="text-sm text-slate-400">Ask the customer for their 6-digit closure code.</p>
                        <Input value={closureCode} onChange={e => setClosureCode(e.target.value)} placeholder="000000" className="bg-slate-800 border-slate-700 font-mono text-2xl tracking-widest text-center" maxLength={6} data-testid="closure-input"/>
                        <Button onClick={submitClosure} className="bg-emerald-600 hover:bg-emerald-700" data-testid="closure-submit-btn">Verify & Complete</Button>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
              </Card>
            ))}
          </TabsContent>

          <TabsContent value="history" className="space-y-2 mt-3" data-testid="history-list">
            {history.length === 0 && <div className="text-sm text-slate-400">No past jobs yet.</div>}
            {history.map(h => (
              <Card key={h.id} className="p-3 bg-slate-800 border-slate-700 flex items-center justify-between text-sm">
                <div><div className="font-mono text-xs">{h.booking?.code}</div><div className="text-xs text-slate-400">{h.booking?.pricing?.service_label} • {h.booking?.scheduled_date}</div></div>
                <span className={`text-xs px-2 py-1 rounded ${h.status === "checked_out" ? "bg-emerald-500/20 text-emerald-300" : "bg-slate-700 text-slate-400"}`}>{h.status}</span>
              </Card>
            ))}
          </TabsContent>

          <TabsContent value="payouts" className="space-y-2 mt-3" data-testid="payouts-list">
            {payouts.length === 0 && <div className="text-sm text-slate-400">No payouts yet.</div>}
            {payouts.map(p => (
              <Card key={p.id} className="p-3 bg-slate-800 border-slate-700 flex items-center justify-between">
                <div><div className="font-mono text-xs text-slate-400">{p.booking_id.slice(0,8)}</div><div className="font-bold flex items-center gap-1"><Wallet size={14}/>₹{p.net}</div></div>
                <span className={`text-xs px-2 py-1 rounded ${p.status === "paid" ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`}>{p.status}</span>
              </Card>
            ))}
          </TabsContent>

          <TabsContent value="ratings" className="space-y-2 mt-3" data-testid="ratings-list">
            {ratings.length === 0 && <div className="text-sm text-slate-400">No ratings yet.</div>}
            {ratings.map(r => (
              <Card key={r.id} className="p-3 bg-slate-800 border-slate-700">
                <div className="flex items-center gap-1 text-yellow-400"><Star size={14} fill="currentColor"/><span className="font-bold">{r.stars}/5</span><span className="text-xs text-slate-500 ml-2 font-mono">{r.booking_id.slice(0,8)}</span></div>
                {r.review && <div className="text-sm text-slate-300 mt-1">"{r.review}"</div>}
              </Card>
            ))}
          </TabsContent>

          <TabsContent value="more" className="space-y-4 mt-3">
            <section>
              <div className="font-bold text-sm mb-2 flex items-center gap-2"><User size={14}/>Profile</div>
              <Card className="p-3 bg-slate-800 border-slate-700 text-xs space-y-1">
                <div className="flex justify-between"><span className="text-slate-400">Phone</span><span className="font-mono">{me.phone}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Category</span><span>{me.category}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">City</span><span>{me.city}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Experience</span><span>{me.experience_years}y</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Skills</span><span className="text-right">{(me.skills||[]).join(", ") || "—"}</span></div>
              </Card>
            </section>

            <section>
              <div className="font-bold text-sm mb-2 flex items-center gap-2"><Bell size={14}/>Notifications</div>
              <div className="space-y-1" data-testid="notifs-list">
                {notifs.length === 0 && <div className="text-xs text-slate-400">No notifications.</div>}
                {notifs.slice(0,10).map(n => (
                  <Card key={n.id} className="p-2.5 bg-slate-800 border-slate-700">
                    <div className="text-xs font-bold">{n.title}</div>
                    <div className="text-xs text-slate-400">{n.body}</div>
                  </Card>
                ))}
              </div>
            </section>

            <section>
              <div className="font-bold text-sm mb-2 flex items-center gap-2"><MessageSquareWarning size={14}/>Complaints</div>
              <div className="space-y-1" data-testid="complaints-list">
                {complaints.length === 0 && <div className="text-xs text-slate-400">No complaints filed against you — good job!</div>}
                {complaints.map(c => (
                  <Card key={c.id} className="p-2.5 bg-slate-800 border-slate-700">
                    <div className="text-xs text-slate-300">{c.description}</div>
                    <div className="text-[10px] text-slate-500 mt-1">Priority: <b>{c.priority}</b> • Status: {c.status}</div>
                  </Card>
                ))}
              </div>
              <Dialog>
                <DialogTrigger asChild><Button size="sm" variant="outline" className="w-full mt-2 border-slate-600 text-slate-300" data-testid="raise-complaint-btn">Raise Issue</Button></DialogTrigger>
                <DialogContent className="bg-slate-900 border-slate-700 text-slate-100">
                  <DialogHeader><DialogTitle>Raise an Issue</DialogTitle></DialogHeader>
                  <Label>Category</Label>
                  <Select value={newComplaint.category} onValueChange={v => setNewComplaint(x => ({...x, category: v}))}>
                    <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="complaint-cat"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="payment">Payment</SelectItem>
                      <SelectItem value="customer_behavior">Customer Behavior</SelectItem>
                      <SelectItem value="site_safety">Site Safety</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <Label>Describe</Label>
                  <Textarea value={newComplaint.description} onChange={e => setNewComplaint(x => ({...x, description: e.target.value}))} className="bg-slate-800 border-slate-700" data-testid="complaint-desc"/>
                  <Button className="bg-orange-500 hover:bg-orange-600" onClick={submitComplaint} data-testid="complaint-submit">Submit</Button>
                </DialogContent>
              </Dialog>
            </section>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
